
import Text from 'components/_UI/Text'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Row } from 'styled/alignment/Row'
import colors from 'theme/colors'
import { Column } from 'styled/alignment/Column'
import Fieldset from 'components/_UI/Fieldset'
import breakpoints from 'theme/breakpoints'
import TicketValidationModal from './tickets/TicketValidationModal'
import fieldValidations from 'helpers/fieldValidations'
import { useForm } from 'react-hook-form'
import { FullyCentered } from 'styled/alignment/Center'
import Loader from 'components/_UI/Loader'
import ticketsApi from 'api/requests/tickets'
import Modal from 'components/_UI/Modal'
import { CheckCircle } from 'react-feather'
import currency from 'helpers/currency'
import buttonText from 'texts/buttons/buttonText'
import { useInvisibleButton } from 'context/general/InvisibleButton'
import EventsList from './events/EventsList'
import LinkPage from 'components/_UI/LinkPage'
import ParticipantsListModal from './events/ParticipantsListModal'
import Icon from 'components/_UI/Icon'
import Ilustration from 'components/_UI/Ilustration'

export default function Checkin({
    minimal = false,
    withParticipants = false,
}) {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            code: ''
        }
    });

    const [isValidatingTicket, setIsValidatingTicket] = useState(false)
    const [loading, setLoading] = useState(false)
    const [validationError, setValidationError] = useState(false)
    const [qrCodeError, setQrCodeError] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [isEventsVisible, setIsEventsVisible] = useState(false)
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [showParticipantsVisible, setShowParticipantsVisible] = useState(false)
    const { invisibleButton, setInvisibleButton } = useInvisibleButton()

    function showQrScanner() {
        setInvisibleButton(true)
        setIsValidatingTicket(true)
    }

    function showEventsModal() {
        setInvisibleButton(true)
        setIsEventsVisible(true)
    }

    function closeModal() {
        setInvisibleButton(false)
        setIsValidatingTicket(false)
    }

    async function validateTicket({ code }) {
        if (code.length !== 6) {
            return
        }

        setLoading(true)

        try {
            const { data } = await ticketsApi.checkIn({ code })
            setValidationError(false);
            setQrCodeError('');
            setShowSuccessModal(data?.data);
            reset({ code: '' });

        } catch (error) {
            setValidationError(error?.response?.data?.message)
        } finally {
            setLoading(false)
        }
    }

    function onEventClick(e) {
        setSelectedEvent(e);
        setIsEventsVisible(false);
        setShowParticipantsVisible(true);
    }

    return (
        <>
            <PageContainer>
                <TicketValidationModal
                    isOpen={isValidatingTicket}
                    onClose={() => closeModal()}
                    onSucess={(e) => {
                        setQrCodeError('')
                        setValidationError(false)
                        setShowSuccessModal(e)
                    }}
                    onError={(error) => setQrCodeError(error?.response?.data?.message || 'Houve um erro ao ler o QR Code')}
                />

                <PageHeader>
                    <HeaderAccent />
                    <HeaderIcon>
                        <Icon name='check' color={colors.occasionalPurple} />
                    </HeaderIcon>
                    <HeaderText>
                        <PageTitle>Validação</PageTitle>
                        <PageDescription>
                            Verifique se ingressos e/ou produtos são válidos de forma rápida e prática.
                        </PageDescription>
                    </HeaderText>
                </PageHeader>

                <ContentLayout>
                    {!minimal && (
                        <IllustrationArea>
                            <Ilustration name='checkIn' />
                        </IllustrationArea>
                    )}

                    <FormSection minimal={minimal}>
                        <SectionLabel>
                            <SectionIcon>
                                <Icon name='check' color={colors.occasionalPurple} />
                            </SectionIcon>
                            Código de verificação
                        </SectionLabel>

                        <FormArea onSubmit={handleSubmit(validateTicket)}>
                            <FieldWrapper>
                                <Fieldset
                                    label='Código de validação'
                                    labelPosition
                                    placeholder='000000'
                                    layout='straight-center'
                                    name='code'
                                    register={register}
                                    validations={fieldValidations.code('código')}
                                />
                            </FieldWrapper>

                            {errors.code && !validationError && (
                                <ErrorMessage>
                                    <Text color={colors.danger} name='small'>{errors.code?.message}</Text>
                                </ErrorMessage>
                            )}
                            {!errors.code && validationError && (
                                <ErrorMessage>
                                    <Text color={colors.danger} name='small'>{validationError}</Text>
                                </ErrorMessage>
                            )}

                            {loading ? (
                                <FullyCentered>
                                    <Loader />
                                </FullyCentered>
                            ) : (
                                <ActionsArea>
                                    <PrimaryButton type='submit'>
                                        Validar código
                                    </PrimaryButton>

                                    <Divider>
                                        <DividerLine />
                                        <DividerText>ou</DividerText>
                                        <DividerLine />
                                    </Divider>

                                    <SecondaryButton type='button' onClick={showQrScanner}>
                                        Escanear QRCode
                                    </SecondaryButton>

                                    {qrCodeError && (
                                        <ErrorMessage>
                                            <Text color={colors.danger} name='small'>{qrCodeError}</Text>
                                        </ErrorMessage>
                                    )}

                                    {withParticipants && (
                                        <ParticipantsLink onClick={showEventsModal}>
                                            <Text>Você pode acompanhar a lista de participantes</Text>&#160;
                                            <LinkPage size='default'>clicando aqui.</LinkPage>
                                        </ParticipantsLink>
                                    )}
                                </ActionsArea>
                            )}
                        </FormArea>
                    </FormSection>
                </ContentLayout>
            </PageContainer>

            <Modal isOpen={showSuccessModal} closeButton onClose={() => setShowSuccessModal(false)}>
                <SuccessHeader>
                    <SuccessIcon>
                        <CheckCircle size={20} color={colors.success} />
                    </SuccessIcon>
                    <Text color={colors.success} name='subtitle' paddingBottom={0}>
                        Ingresso/Produto validado
                    </Text>
                </SuccessHeader>

                <InfoCard>
                    {!!showSuccessModal?.event_name && (
                        <InfoRow>
                            <InfoLabel>Evento</InfoLabel>
                            <InfoValue>{showSuccessModal?.event_name}</InfoValue>
                        </InfoRow>
                    )}
                    <InfoRow>
                        <InfoLabel>Participante</InfoLabel>
                        <InfoValue>{showSuccessModal?.participant_name}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>{showSuccessModal?.type === 'ticket' ? 'Ingresso' : 'Produto'}</InfoLabel>
                        <InfoValue>{showSuccessModal?.product_name}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Preço</InfoLabel>
                        <InfoValue highlight>{currency.format(Number(showSuccessModal?.price))}</InfoValue>
                    </InfoRow>
                </InfoCard>

                <ModalButton onClick={() => setShowSuccessModal(false)}>
                    {buttonText.ok}
                </ModalButton>
            </Modal>

            <Modal
                isOpen={isEventsVisible}
                closeButton
                size='small'
                onClose={() => setIsEventsVisible(false)}
            >
                <Column paddingTop='.5rem' paddingBottom='.5rem' paddingLeft='.5rem' paddingRight='.5rem' marginTop='30px'>
                    <EventsList onEventClick={(e) => onEventClick(e)} />
                </Column>
            </Modal>

            {showParticipantsVisible && (
                <Column marginTop='200px'>
                    <ParticipantsListModal
                        event={selectedEvent}
                        visible={showParticipantsVisible}
                        onClose={() => setShowParticipantsVisible(false)}
                        onBack={() => {
                            setShowParticipantsVisible(false)
                            setIsEventsVisible(true)
                        }}
                    />
                </Column>
            )}
        </>
    )
}

const PageContainer = styled.section`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-bottom: 16px;
    position: relative;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px;
    }
`;

const HeaderAccent = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${colors.occasionalPurple};
    border-radius: 12px 0 0 12px;
`;

const HeaderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${colors.occasionalPurple}10;
    flex-shrink: 0;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PageTitle = styled.h1`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
`;

const PageDescription = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.5;
`;

const ContentLayout = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    flex-direction: column-reverse;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
    }
`;

const IllustrationArea = styled.div`
    display: none;
    flex-shrink: 0;

    & > svg, & > img {
        height: 280px;
        width: auto;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const FormSection = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    width: 100%;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px;
        width: ${({ minimal }) => minimal ? '100%' : '50%'};
    }
`;

const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const FormArea = styled.form`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const FieldWrapper = styled.div`
    width: 100%;
    text-align: center;

    input:not([class*="css-"]):not([id*="react-select"]) {
        background: ${colors.night}08 !important;
        border: 1.5px solid ${colors.night}22 !important;
        border-radius: 10px !important;
        padding: 12px 14px !important;
        font-size: 14px !important;
        color: ${colors.nightDark} !important;
        transition: all 0.15s ease !important;

        &::placeholder {
            color: ${colors.night}88 !important;
        }

        &:focus {
            border-color: ${colors.occasionalPurple} !important;
            box-shadow: 0 0 0 3px ${colors.occasionalPurple}18 !important;
            background: #fff !important;
        }
    }

    label {
        font-size: 13px !important;
        font-weight: 600 !important;
        color: ${colors.nightDark} !important;
        margin-bottom: 4px !important;
    }
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px;
    background: ${colors.danger}0A;
    border-radius: 6px;

    & > p {
        font-size: 12px;
        font-weight: 500;
    }
`;

const ActionsArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;
`;

const PrimaryButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.secondaryPurple};
    }
`;

const SecondaryButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    border: 1.5px solid ${colors.occasionalPurple}44;
    border-radius: 10px;
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.occasionalPurple}08;
        border-color: ${colors.occasionalPurple}66;
    }
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const DividerLine = styled.div`
    flex: 1;
    height: 1px;
    background: ${colors.night}18;
`;

const DividerText = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ParticipantsLink = styled(Row)`
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 8px;
    cursor: pointer;

    & > a, & > p {
        width: auto;
        font-size: 13px;
    }
`;

const SuccessHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 16px;
`;

const SuccessIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${colors.success}15;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const InfoCard = styled.div`
    background: ${colors.night}06;
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
`;

const InfoLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const InfoValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ highlight }) => highlight ? colors.occasionalPurple : colors.nightDark};
`;

const ModalButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    margin-top: 16px;

    &:hover {
        background: ${colors.secondaryPurple};
    }
`;
